import React, { useState, useEffect, useRef, useCallback } from "react";
import { withRouter } from "react-router-dom";
import { Alert, Nav, NavItem, NavLink, Row, Col, Card, CardBody, CardTitle, CardText } from "reactstrap";
import { service as neoImporterService } from "../redux";
import { CollapsibleResult, Icon } from "zina-dogi-react";
import * as _ from "lodash";

const NeoImporterResults = (props) => {
    const {
        results,
        celery_tasks,
        processUrl,
        detail_file_url,
        results_file_url,
        contentType,
        pk_placeholder,
        match,
        history,
        backwardCallback
        // validate, // unused
        // validate
    } = props;

    const [loaded, setLoaded] = useState(false);
    const [totalLines, setTotalLines] = useState({
        errors: 0,
        infos: 0,
        warnings: 0,
        total: 0
    });
    const [status, setStatus] = useState(null);
    const [successLines, setSuccessLines] = useState([]);
    const [errorLines, setErrorLines] = useState([]);
    const [validation, setValidation] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [isMultiTab, setIsMultiTab] = useState(false);
    // const [validateEnd, setValidateEnd] = useState(false); // Unused state var
    const [serviceResult, setServiceResult] = useState(null);
    const [params, setParams] = useState(null); // To store calculated params like grouping info

    const [hasBackwardButton, setHasBackwardButton] = useState(false);
    const [backwardData, setBackwardData] = useState(null);
    const [backwardUrl, setBackwardUrl] = useState(null);

    const intervalRef = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Initial setup
    useEffect(() => {
        if (results) {
            setValidation(false);
        }

        if (celery_tasks) {
            initProcessInterval();
        } else {
            // setValidateEnd(true);
            getImporterResults();
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [celery_tasks, results]);

    const getImporterResults = useCallback(() => {
        const url = results_file_url.replace(pk_placeholder, match.params.id);
        if (url) {
            neoImporterService
                .resultsFileUploaded(url)
                .then(importer_result => {
                    if (isMounted.current) {
                        const { 
                            results, 
                            enable_backward = false, 
                            backward_data = null, 
                            backward_url = null
                        } = importer_result;
                        
                        let res = results;
                        if (Array.isArray(results)) {
                            setServiceResult(results);
                            setIsMultiTab(results.length > 1);
                            res = results[activeTab];
                        } else {
                            setServiceResult(null);
                        }
                        if (enable_backward) { 
                            res = { ...res, backward_url, backward_data };
                        }
                        showResults(res);
                    }
                })
                .catch(error => {
                    console.log({ error });
                });
        }
    }, [results_file_url, pk_placeholder, match.params.id, activeTab]);

    const initProcessInterval = useCallback(() => {
        const call = (fn) => {
            neoImporterService
                .getStatusImport(detail_file_url.replace(pk_placeholder, match.params.id))
                .then(process => {
                    if (!isMounted.current) return;
                    const { validate_end, state } = process;

                    // We need current isMultiTab and activeTab, which might be stale in closure if not careful.
                    // However, getStatusImport response format vs isMultiTab dependency is tricky.
                    // Assuming 'process.data' structure here respects the contract.

                    // Note: In Class Component, 'this.state.isMultiTab' was read.
                    // Here we might need to rely on what 'process' returns or previous fetch.
                    // If 'process.data' is array, it likely means multi-tab.

                    const isResultArray = Array.isArray(process.data);
                    const data = isResultArray ? process.data[activeTab] : process.data;

                    setTotalLines(computeTotalLines(data));
                    // setValidateEnd(validate_end);
                    setStatus(state);

                    if (validate_end) {
                        fn && fn();
                        getImporterResults();
                        if (intervalRef.current) clearInterval(intervalRef.current);
                    }
                })
                .catch(error => {
                    console.log({ error });
                });
        };

        call();
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            call();
        }, 5000);
    }, [detail_file_url, pk_placeholder, match.params.id, activeTab, getImporterResults]);

    const showResults = (resData) => {
        if (resData) {
            const { 
                data, 
                data_summary, 
                columns_to_group, 
                columns_mapping, 
                single_elements, 
                grouped_fields,
                backward_data = null,
                backward_url = null,
            } = resData;

            const newParams = {
                grouped: Object.keys(columns_to_group).length,
                columnsGrouped: Object.keys(columns_to_group),
                columnMapping: columns_mapping,
                singleElementsNames: _.invert(single_elements),
                GroupedFields: _.invert(grouped_fields)
            };
            setParams(newParams);
            setLoaded(true);
            setHasBackwardButton(Boolean(backward_url || backward_data));
            setBackwardData(backward_data);
            setBackwardUrl(backward_url);

            if (data) {
                setSuccessLines(data.infos);
                setErrorLines(data.errors);
                setTotalLines(computeTotalLines(data_summary));
                setSpecificCells(agroup(data.specific_cells, 2));
            }
        }
    };

    const [specificCells, setSpecificCells] = useState([]); // Helpers for rendering

    const computeTotalLines = (data) => {
        if (!data) return { errors: 0, infos: 0, warnings: 0, total: 0 };
        let { errors, infos, warnings } = data;
        return {
            errors,
            infos,
            warnings,
            total: errors + infos + warnings
        };
    };

    const agroup = (data, size) => {
        let res = [];
        if (data) {
            // Avoid mutating original data array if it's reused. 
            // Splice mutates. Let's copy it.
            let dataCopy = [...data];
            while (dataCopy.length > 0) {
                res.push(dataCopy.splice(0, size));
            }
        }
        return res;
    };

    const toggle = (tab) => {
        if (activeTab !== tab && isMultiTab) {
            setActiveTab(tab);
            // We need to re-show results for the new tab, preserving top-level backward fields
            if (serviceResult) {
                const tabData = (backwardUrl || backwardData)
                    ? { ...serviceResult[tab], backward_url: backwardUrl, backward_data: backwardData }
                    : serviceResult[tab];
                showResults(tabData);
            }
        }
    };

    const processFile = () => {
        setTotalLines({
            errors: 0,
            infos: 0,
            warnings: 0,
            total: 0
        });
        neoImporterService
            .processFile(processUrl.replace(pk_placeholder, match.params.id))
            .then(data => {
                if (isMounted.current) {
                    history.push(match.url.replace("validate", "results"));
                }
            });
    };

    const backward = async () => {
        try {
            await backwardCallback(backwardUrl, backwardData);
        } catch (error) {
            console.error("[NeoImporterResults] backwardCallback failed:", error);
        }
    };

    const computeHead = (data) => {
        if (data && data.length > 0) {
            return (
                <div className="specific_cells" style={{ padding: "16px" }}>
                    {data.map((group, i) => (
                        <Row key={`specific_cell_group_${i}`}>
                            {group.map((cell, j) => {
                                return (
                                    <Col className="list-group-item" key={`specific_cell_${j}`}>
                                        <b>{cell.label}:</b>
                                        <span> {cell.value}</span>
                                        {!!cell.error && (
                                            <small className="invalid-feedback" style={{ display: "block" }}>
                                                Danger: {cell.error}:{" "}
                                                {`cell ${cell.cell.col + 1} - ${cell.cell.row + 1}`}
                                            </small>
                                        )}
                                        {!!cell.warning && (
                                            <small
                                                className="invalid-feedback"
                                                style={{
                                                    display: "block",
                                                    color: "var(--orange, orange)"
                                                }}
                                            >
                                                Warning: {cell.warning}: {`cell ${cell.cell.col + 1}`} -
                                                {cell.cell.row + 1}
                                            </small>
                                        )}
                                    </Col>
                                );
                            })}
                        </Row>
                    ))}
                </div>
            );
        }
        return null;
    };

    const computeTabs = () => {
        if (serviceResult) {
            const importers = serviceResult;
            return (
                <Nav tabs>
                    {importers &&
                        importers.map((imp, i) => {
                            const { type } = imp.file_upload_history;
                            return (
                                <NavItem key={`importer-tab-${i}`}>
                                    <NavLink
                                        className={activeTab === i ? "active" : ""}
                                        onClick={() => {
                                            toggle(i);
                                        }}
                                    >
                                        {type}
                                    </NavLink>
                                </NavItem>
                            );
                        })}
                </Nav>
            );
        }
    };

    const generateCollapsibleResults = (lines, type, verb) => {
        if (lines && params) {
            const results = lines.map((elem, i) => {
                if (type === "success") {
                    elem["group"] = { ...elem };
                }
                let uniqueSlug = "";
                let elements = {};

                if (params.grouped) {
                    uniqueSlug = params.columnsGrouped
                        .map(key => elem.group[params.columnMapping[key]])
                        .join("");
                    Object.keys(elem.group).forEach(key => {
                        if (Object.keys(params.singleElementsNames).indexOf(key) !== -1) {
                            elements[key] = [params.singleElementsNames[key], elem.group[key]];
                        }
                    });
                } else {
                    if (type === "error") {
                        elem.importer_extra_data = {};
                        elem.importer_extra_data.user_line_number = elem.first_line;
                    }

                    uniqueSlug = elem.importer_extra_data && elem.importer_extra_data.user_line_number
                        ? elem.importer_extra_data.user_line_number
                        : `unknown_${i}`;

                    Object.keys(elem.group).forEach(key => {
                        if (Object.keys(params.singleElementsNames).indexOf(key) !== -1) {
                            elements[key] = [params.singleElementsNames[key], elem.group[key]];
                        }
                    });
                }
                return (
                    <CollapsibleResult
                        key={uniqueSlug}
                        title={
                            params.grouped
                                ? `The ${params.columnsGrouped.join(", ")} ${uniqueSlug} ${verb}`
                                : `The row ${uniqueSlug} ${verb}`
                        }
                        type={type}
                    >
                        <div className="row">
                            {type === "error" ? (
                                <div className="col-md-12">
                                    <ul className="list-group">
                                        {elem.msg && elem.msg.map((msg, j) => (
                                            <li
                                                className="list-group-item list-group-item-danger"
                                                key={`list-group-${msg.line}-${i}-${j}`}
                                            >
                                                <Icon
                                                    style={{
                                                        paddingRight: "5px"
                                                    }}
                                                    icon="times-circle"
                                                />
                                                <strong>{msg.field}</strong> {msg.msg}
                                                <i> (Line:{msg.line})</i>
                                            </li>
                                        ))}
                                    </ul>
                                    <br />
                                </div>
                            ) : null}
                            {Object.values(elements).map((info, index) => (
                                <div className="col-md-6" key={index}>
                                    <strong>{info[0]} : </strong>
                                    {info[1]}
                                </div>
                            ))}
                            {params.grouped ? (
                                <div className="col-md-12">
                                    <table className="table" style={{ paddingTop: "10px" }}>
                                        <thead>
                                            <tr>
                                                {Object.values(params.GroupedFields).map(name => (
                                                    <th key={name}>{name}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {elem.group[Object.keys(params.GroupedFields)[0]] &&
                                                _.range(elem.group[Object.keys(params.GroupedFields)[0]].length).map(
                                                    (index, line) => (
                                                        <tr key={line}>
                                                            {Object.keys(params.GroupedFields).map(slug => (
                                                                <td key={`${slug}-${line}`}>{elem.group[slug][index]}</td>
                                                            ))}
                                                        </tr>
                                                    )
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    </CollapsibleResult>
                );
            });
            return results;
        }
        return null;
    };

    const collapsibleErrorResults = generateCollapsibleResults(
        errorLines,
        "error",
        " was rejected"
    );
    const collapsibleSuccessResults = generateCollapsibleResults(
        successLines,
        "success",
        validation ? " was validated" : " was imported"
    );

    const processButton = (
        <div className="row">
            <div className="col-md-12" style={{ marginTop: "10px" }}>
                <div className="float-right">
                    <p>
                        Only {contentType} validated will be processed
                        <button
                            style={{ marginLeft: "8px" }}
                            onClick={processFile}
                            className="btn btn-success float-right"
                        >
                            Process
                        </button>
                        {hasBackwardButton && backwardCallback && <button
                            style={{ marginLeft: "8px" }}
                            onClick={backward}
                            className="btn btn-danger float-right"
                        >
                            Go Back
                        </button>}
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="Importer">
            {serviceResult && computeTabs()}
            {loaded && !validation && (
                <Alert color="success">The importer has finished the process.</Alert>
            )}
            <div className="row" style={{ paddingBottom: "15px" }}>
                <h3>Results Summary{"   "}</h3>
            </div>
            <div className="row d-flex" style={{ paddingBottom: "20px", alignItems: 'stretch' }}>
                <div className="col-md-3 d-flex align-items-stretch">
                    <div style={{ width: '100%' }}>
                        <Card className="h-100 text-white bg-secondary text-center">
                            <CardBody>
                                <CardTitle tag="h2">{totalLines.total}</CardTitle>
                                <CardText>
                                    {contentType} in total
                                </CardText>
                            </CardBody>
                        </Card>
                    </div>
                </div>
                {totalLines.infos > 0 ? (
                    <div className="col-md-3 d-flex align-items-stretch">
                        <div style={{ width: '100%' }}>
                            <Card className="h-100 text-white bg-success text-center">
                                <CardBody>
                                    <CardTitle tag="h2">{totalLines.infos}</CardTitle>
                                    <CardText>
                                        {contentType} validated
                                    </CardText>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                ) : null}
                {totalLines.errors > 0 ? (
                    <div className="col-md-3 d-flex align-items-stretch">
                        <div style={{ width: '100%' }}>
                            <Card className="h-100 text-white bg-danger text-center">
                                <CardBody>
                                    <CardTitle tag="h2">{totalLines.errors}</CardTitle>
                                    <CardText>
                                        {contentType} rejected
                                    </CardText>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                ) : null}
            </div>
            {!loaded && (
                <div className="row" style={{ paddingBottom: "20px" }}>
                    <div className="col-md-12">
                        <div className="importer-infinite-loader"></div>
                    </div>
                </div>
            )}
            {computeHead(specificCells)}
            {loaded && validation && status !== "Processed" && processButton}
            <br />
            <div className="row">
                <div className="col-md-12">{collapsibleErrorResults}</div>
                <div className="col-md-12">{collapsibleSuccessResults}</div>
            </div>
            {loaded &&
                validation &&
                status !== "Processed" &&
                totalLines.errors > 0 &&
                processButton}
        </div>
    );
};

export default withRouter(NeoImporterResults);
